import { useCallback, useEffect, useMemo, useState } from 'react'
import { BookOpen, FileSpreadsheet, Layers, UserRound } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import DateRangeField from '../../components/DateRangeField'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import OutcomeShare from '../../components/OutcomeShare'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import Select from '../../components/Select'
import StatStrip from '../../components/StatStrip'
import { EmptyState, ErrorState } from '../../components/States'
import TabBar from '../../components/TabBar'
import ProgramReportPeople from './ProgramReportPeople'
import ProgramCertificatesCard from '../certificates/ProgramCertificatesCard'
import { useToast } from '../../components/toastContext'
import useGoBack from '../../components/useGoBack'
import useHashTab from '../../components/useHashTab'
import useListParams from '../../components/useListParams'
import { exportProgramReport, getReportsPage, tryGetProgramSummary } from './reportsService'
import { reportsStrings as S } from './strings'
import './ProgramReportsPage.css'
import { serverDetail } from '../../lib/i18n'
import { kit } from '../../components/strings'
import { displayDate } from '../../lib/dates'

/**
 * What this screen keeps in its own address — the programme and the two dates.
 *
 * `?program=` was already there: the programmes list links here from a row's report button,
 * and the picker starts on that programme instead of on nothing. THE DATES WERE NOT, and
 * they are half of what the report says — every share, every count and every course row is
 * for the window they name — so `/programs/reports?program=7` opened on the all-time figures
 * however the reader had narrowed them, and a Back out of the screen lost the window again.
 * `?program=7&from=2026-01-01` IS the report. Empty means all time and stays out of the URL.
 * Written with `replace`, so picking three programmes in a row does not fill the back button
 * with them. Module-level, as `useListParams` requires.
 *
 * `page` is the courses table's own — it is listed so the three setters below can put it
 * back to 1, which is the whole of the reset here: a new programme replaces the rows while
 * the table is unmounted behind the loader, so it never sees the change itself.
 */
const LIST_PARAMS = { program: '', from: '', to: '', page: 1 }

/** The legacy report's five tabs, in its order. The open one is the address's `#hash`. */
const TABS = ['info', 'courses', 'survey', 'results', 'certificates']

/**
 * تقرير البرامج التدريبية — the legacy `/LearningProgramsReports`.
 *
 * Built rather than wired (R5): the route rendered a placeholder.
 *
 * One tab per part of the report, as the legacy page had them. The programme's outcome — the
 * four shares, the counts, its courses and its survey answers — comes from `GetProgramDetails`.
 * The people behind the shares (`ProgramReportPeople`) and the certificates
 * (`ProgramCertificatesCard`) load when their tab is opened, not with the report.
 */
export default function ProgramReportsPage() {
  const toast = useToast()

  /* What the report is OF — in the address, so it survives a refresh and can be sent to
     someone. See components/useListParams.js. */
  const [params, setParams] = useListParams(LIST_PARAMS)
  const [tab, setTab] = useHashTab('info', TABS)

  const [programs, setPrograms] = useState([])
  const [programsLoaded, setProgramsLoaded] = useState(false)
  const programId = params.program
  const fromDate = params.from
  const toDate = params.to
  /* Patched rather than rewritten — the old setter replaced the whole search string, so
     choosing a programme would have taken the dates down with it — and each one starts the
     courses back at page 1, since it is a different set of courses. The range is one control,
     so both ends are written together. */
  const setProgramId = useCallback((next) => setParams({ program: next, page: 1 }), [setParams])
  const setRange = useCallback(({ from, to }) => setParams({ from, to, page: 1 }), [setParams])
  /* Back to the programmes list this report was opened from — the row's "report" action
     on /programs — or to that list when it was reached by its own address. The other
     report pages carry this; this one was the only way in with no way out but the menu. */
  const goBack = useGoBack('/programs')

  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    getReportsPage()
      .then((page) => {
        setPrograms(page.programs)
        setProgramsLoaded(true)
      })
      .catch((error) => toast.error(serverDetail(error?.message) || S.fail.page))
  }, [toast])

  /* A window that ends before it starts has nothing in it. The report ran anyway and said the
     programme had no courses. And an id that is no programme showed an empty report under a
     generic title, with nothing to say it was the id that was wrong. */
  const reversed = Boolean(fromDate && toDate && toDate < fromDate)
  const program = useMemo(
    () => programs.find((option) => option.value === String(programId)) ?? null,
    [programs, programId],
  )
  const unknown = Boolean(programId && programsLoaded && !program)

  useEffect(() => {
    setLoadError(null)
    if (!programId || reversed || unknown) {
      setSummary(null)
      // A load begun before the programme list arrived is cancelled — and so is its loader.
      setLoading(false)
      return undefined
    }

    let cancelled = false
    setLoading(true)

    tryGetProgramSummary({ programId, fromDate, toDate }).then((outcome) => {
      if (cancelled) return
      setLoading(false)
      if (!outcome.ok) {
        setSummary(null)
        setLoadError(serverDetail(outcome.message) || S.fail.page)
        return
      }
      setSummary(outcome.summary)
    })

    return () => {
      cancelled = true
    }
  }, [programId, fromDate, toDate, reversed, unknown, reload])

  const handleExport = useCallback(async () => {
    if (!programId || reversed || unknown) return
    setExporting(true)
    try {
      await exportProgramReport({ programId, fromDate, toDate })
      toast.success(S.exportSuccess)
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.fail.export)
    } finally {
      setExporting(false)
    }
  }, [programId, fromDate, toDate, reversed, unknown, toast])

  const courseColumns = useMemo(
    () => [
      { key: 'name', header: S.courses.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'startDate',
        header: S.courses.startDate,
        render: (row) => (
          <span dir="ltr" className="ud__date">
            {displayDate(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.courses.endDate,
        render: (row) => (
          <span dir="ltr" className="ud__date">
            {displayDate(row.endDate)}
          </span>
        ),
      },
      { key: 'topicsCount', header: S.courses.topics, align: 'center' },
      { key: 'success', header: S.courses.success, align: 'center' },
      { key: 'failure', header: S.courses.failure, align: 'center' },
      { key: 'absent', header: S.courses.absent, align: 'center' },
    ],
    [],
  )

  const surveyColumns = useMemo(
    () => [
      { key: 'question', header: S.survey.question, render: (row) => <bdi>{row.question}</bdi> },
      { key: 'answer', header: S.survey.answer, render: (row) => <bdi>{row.answer}</bdi> },
      { key: 'count', header: S.survey.count, align: 'center' },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        onBack={goBack}
        actions={
          summary && (
            <Button variant="soft" onClick={handleExport} busy={exporting}>
              <FileSpreadsheet size={16} strokeWidth={1.75} />
              {S.export}
            </Button>
          )
        }
      />

      <FilterBar>
        <Select
          value={programId}
          onChange={setProgramId}
          options={programs}
          placeholder={S.filter.program}
          aria-label={S.filter.program}
        />
        <DateRangeField
          value={{ from: fromDate, to: toDate }}
          onChange={setRange}
          labels={{ from: S.filter.from, to: S.filter.to }}
        />
      </FilterBar>

      {!programId && (
        <InfoNote icon={BookOpen}>
          <bdi>{S.chooseProgram}</bdi>
        </InfoNote>
      )}

      {unknown && <EmptyState title={S.notFound} description={S.notFoundHint} />}
      {!unknown && reversed && <ErrorState title={S.reversedRange} />}

      {/* Stays up while a new programme loads, so the tab in use is not lost under the loader. */}
      {programId && !unknown && !reversed && (
        <div className="program-report__tabs">
          <TabBar
            items={TABS.map((id) => ({ id, label: S.tabs[id] }))}
            value={tab}
            onChange={setTab}
            ariaLabel={S.title}
          />
        </div>
      )}

      {loading && <PageLoader />}
      {!loading && loadError && (
        <ErrorState
          title={S.fail.page}
          description={loadError !== S.fail.page ? loadError : undefined}
          action={<Button onClick={() => setReload((value) => value + 1)}>{kit.retry}</Button>}
        />
      )}

      {!loading && summary && tab === 'info' && (
        <>
          {/* The four shares this programme actually produced — the same component the
              programmes list hides for want of an aggregate. */}
          <OutcomeShare shares={summary.outcome} labels={S.shares} />

          <StatStrip
            label={S.stats.caption}
            items={[
              { id: 'courses', icon: BookOpen, label: S.stats.courses, value: summary.coursesCount, selectable: false },
              { id: 'groups', icon: Layers, label: S.stats.groups, value: summary.groupsCount, selectable: false },
              { id: 'users', icon: UserRound, label: S.stats.users, value: summary.usersCount, selectable: false },
            ]}
          />
        </>
      )}

      {!loading && summary && tab === 'courses' && (
        <Card title={S.courses.title}>
          <DataTable
            columns={courseColumns}
            rows={summary.courses}
            controls={false}
            /* The programme and the dates are the filter and they sit above the table,
               where it cannot see them: page 3 of one programme's courses is not a page
               of another's. The setters above carry most of this — the table is behind
               the loader while the rows change — and this covers the rest. */
            resetKey={`${programId}|${fromDate}|${toDate}`}
            getRowId={(row) => row.id}
            emptyMessage={S.courses.empty}
          />
        </Card>
      )}

      {/* Always a tab, as in the legacy report — a programme with no survey answers says so. */}
      {!loading && summary && tab === 'survey' && (
        <Card title={S.survey.title}>
          <DataTable
            /* Its own page in the address — `?page` belongs to the courses table. Remembering
               it instead would restore programme 7's page under programme 9, since the key
               carries no programme. */
            pageParam="spage"
            columns={surveyColumns}
            rows={summary.survey}
            controls={false}
            /* Its answers change with the same three, so it starts over with them. */
            resetKey={`${programId}|${fromDate}|${toDate}`}
            getRowId={(row) => row.id}
            emptyMessage={S.survey.empty}
          />
        </Card>
      )}

      {!loading && summary && tab === 'results' && (
        <ProgramReportPeople programId={programId} fromDate={fromDate} toDate={toDate} />
      )}

      {/* Who holds the certificate, and printing it. */}
      {!loading && summary && tab === 'certificates' && program && (
        <ProgramCertificatesCard program={program} fromDate={fromDate} toDate={toDate} />
      )}
    </>
  )
}
